import React, { useState } from 'react';
import { Bell, CheckCircle2, Clock, FileText, Star } from 'lucide-react';
import {
  adicionarDocumentoFornecedor,
  type HomologationStep,
  type Supplier,
  type SupplierPortalNotification,
  type SupplierPortalUpdatePayload,
} from '../api';
import {
  useAtualizarPortalFornecedor,
  useNotificacoesPortalFornecedor,
  usePortalFornecedor,
  useTiposDocumentoFornecedor,
} from '../hooks';
import {
  SupplierBlockingConditionsPanel,
  SupplierHomologationMetadata,
} from '../components/SupplierBlockingConditions';
import {
  SupplierDocumentUpload,
  documentTypeByCode,
} from '../components/SupplierDocumentUpload';
import { SupplierDocumentCard } from '../components/SupplierDocumentCard';
import { SupplierPortalRegistrationForm } from '../components/SupplierPortalRegistrationForm';
import { formatLocalDate } from '../utils/dates';
import {
  supplierDocumentCodes,
  supplierUploadDocumentTypes,
} from '../utils/documents';

const statusLabels: Record<string, string> = {
  em_cadastro: 'Em Cadastro',
  pendente_documentacao: 'Pendente de Documentação',
  em_analise: 'Em Análise',
  em_homologacao: 'Em Homologação',
  homologado: 'Homologado',
  homologado_restricao: 'Homologado com Restrição',
  reprovado: 'Reprovado',
  suspenso: 'Suspenso',
  inativo: 'Inativo',
};

const homologationSteps: Array<{ value: HomologationStep; label: string }> = [
  { value: 'analise_documental', label: 'Análise documental' },
  { value: 'validacao_fiscal', label: 'Validação Fiscal' },
  { value: 'validacao_financeira', label: 'Validação Financeira' },
  { value: 'validacao_juridica', label: 'Validação Jurídica' },
  { value: 'validacao_compliance', label: 'Validação Compliance' },
  { value: 'aprovacao_final', label: 'Aprovação Final' },
];

const approvalDecisionLabels: Record<string, string> = {
  aprovado: 'Aprovado',
  aprovado_restricao: 'Aprovado com restrição',
  reprovado: 'Reprovado',
};

const PortalFornecedorPage: React.FC = () => {
  const { data: supplier, isLoading, refetch } = usePortalFornecedor();
  const { data: documentTypes = [] } = useTiposDocumentoFornecedor();
  const notificationsQuery = useNotificacoesPortalFornecedor(supplier?.id ?? null);
  const updatePortal = useAtualizarPortalFornecedor();
  const [documentType, setDocumentType] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleRegistrationUpdate = async (payload: SupplierPortalUpdatePayload) => {
    setError(null);
    setMessage(null);
    try {
      await updatePortal.mutateAsync(payload);
      await notificationsQuery.refetch();
      setMessage('Dados cadastrais atualizados com sucesso.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao atualizar dados cadastrais.');
      throw err;
    }
  };

  const handleUpload = async () => {
    if (!supplier || !file) return;
    setUploading(true);
    setError(null);
    setMessage(null);
    const selectedDocumentType = documentTypeByCode(documentTypes, documentType);
    if (!selectedDocumentType) {
      setError('Selecione um tipo de documento válido.');
      setUploading(false);
      return;
    }
    try {
      await adicionarDocumentoFornecedor(
        supplier.id,
        {
          documentType,
          name: selectedDocumentType.name,
          expiresAt: selectedDocumentType.validityRule === 'with_expiration'
            ? expiresAt || undefined
            : undefined,
          issuedAt: selectedDocumentType.validityRule === 'periodic_update'
            ? issuedAt || undefined
            : undefined,
        },
        file,
      );
      await refetch();
      setFile(null);
      setExpiresAt('');
      setIssuedAt('');
      setMessage('Documento enviado para análise.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao enviar documento.');
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Carregando portal do fornecedor...</div>;
  }

  if (!supplier) {
    return <div className="p-6 text-sm text-red-600">Não foi possível carregar o fornecedor vinculado.</div>;
  }

  const documentCodes = supplierDocumentCodes(
    supplier.supplierType,
    supplier.requiredDocuments,
    supplier.documents,
    documentTypes,
  );
  const uploadDocumentTypes = supplierUploadDocumentTypes(
    supplier.supplierType,
    supplier.requiredDocuments,
    supplier.documents,
    documentTypes,
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Portal do Fornecedor</h1>
        <p className="text-gray-600 mt-1">Atualização documental, pendências, homologação e avaliações do seu cadastro.</p>
      </div>

      {message && <div role="status" aria-live="polite" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 md:grid-cols-4">
        <PortalMetric icon={<CheckCircle2 />} label="Status" value={statusLabels[supplier.status] || supplier.status} />
        <PortalMetric icon={<FileText />} label="Documentos" value={supplier.documentSummary.total} />
        <PortalMetric icon={<Clock />} label="Pendências" value={supplier.pendingRequiredDocuments.length} />
        <PortalMetric icon={<Star />} label="Avaliação média" value={Number(supplier.averageRating || 0).toFixed(1)} />
      </div>

      <SupplierBlockingConditionsPanel supplier={supplier} title="Pendências para compras e contratos" showEligible />
      <SupplierHomologationMetadata supplier={supplier} />

      <div className="grid gap-6 xl:grid-cols-2">
        <SupplierHomologationProgress supplier={supplier} />
        <SupplierNotificationsPanel
          notifications={notificationsQuery.data ?? []}
          loading={notificationsQuery.isLoading}
          error={notificationsQuery.isError}
        />
      </div>

      <SupplierPortalRegistrationForm
        supplier={supplier}
        submitting={updatePortal.isPending}
        onSubmit={handleRegistrationUpdate}
      />

      <div className="grid gap-6 xl:grid-cols-[420px,1fr]">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div id="supplier-document-upload-portal">
            <SupplierDocumentUpload
              title="Enviar documento"
              documentTypes={uploadDocumentTypes}
              documentType={documentType}
              onDocumentTypeChange={setDocumentType}
              expiresAt={expiresAt}
              onExpiresAtChange={setExpiresAt}
              issuedAt={issuedAt}
              onIssuedAtChange={setIssuedAt}
              file={file}
              onFileChange={setFile}
              onSubmit={handleUpload}
              submitting={uploading}
              submitLabel="Enviar para análise"
              className="border-0 p-0"
            />
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-semibold text-gray-900">{supplier.tradeName || supplier.corporateName}</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {documentCodes.map((type) => (
              <SupplierDocumentCard
                key={type}
                documentCode={type}
                documentTypes={documentTypes}
                documents={supplier.documents}
                required={supplier.requiredDocuments.includes(type)}
                onRenew={() => {
                  setDocumentType(type);
                  document.getElementById('supplier-document-upload-portal')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            ))}
          </div>

          <div className="mt-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Avaliações</h3>
            {supplier.evaluations.length > 0 ? (
              <div className="space-y-2">
                {supplier.evaluations.map((evaluation) => (
                  <div key={evaluation.id} className="rounded-lg border border-gray-200 p-3 text-sm">
                    <div className="font-medium text-gray-900">Média {Number(evaluation.averageScore).toFixed(1)} · {evaluation.result}</div>
                    <div className="text-xs text-gray-500">{formatLocalDate(evaluation.createdAt)}</div>
                    {evaluation.comments && <p className="mt-1 text-gray-700">{evaluation.comments}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Ainda não há avaliações registradas.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

function PortalMetric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between text-sm text-gray-500">
        <span>{label}</span>
        <span className="text-blue-600">{React.cloneElement(icon as React.ReactElement, { className: 'h-5 w-5' })}</span>
      </div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function SupplierHomologationProgress({ supplier }: { supplier: Supplier }) {
  return (
    <section aria-labelledby="supplier-homologation-progress-title" className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 id="supplier-homologation-progress-title" className="font-semibold text-gray-900">Etapas da homologação</h2>
      <p className="mt-1 text-sm text-gray-500">Visão completa das seis validações obrigatórias.</p>
      <ol className="mt-4 space-y-3" aria-label="Fluxo de homologação do fornecedor">
        {homologationSteps.map((step, index) => {
          const approval = supplier.approvals
            .filter((item) => item.step === step.value)
            .slice()
            .sort((left, right) => new Date(right.approvedAt).getTime() - new Date(left.approvedAt).getTime())[0];
          const isCurrent = supplier.homologationStep === step.value && !approval;
          const status = approval ? approvalDecisionLabels[approval.decision] ?? approval.decision : isCurrent ? 'Etapa atual' : 'Pendente';
          return (
            <li key={step.value} data-homologation-step={step.value} className="flex gap-3 rounded-lg border border-gray-200 p-3">
              <span aria-hidden="true" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${approval?.decision === 'reprovado' ? 'bg-red-100 text-red-700' : approval ? 'bg-emerald-100 text-emerald-700' : isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{step.label}</div>
                <div className="text-sm text-gray-600">{status}</div>
                {approval && (
                  <div className="mt-1 text-xs text-gray-500">
                    {formatLocalDate(approval.approvedAt)}
                    {approval.approvedByName ? ` · ${approval.approvedByName}` : ''}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function SupplierNotificationsPanel({
  notifications,
  loading,
  error,
}: {
  notifications: SupplierPortalNotification[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <section aria-labelledby="supplier-notifications-title" className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <Bell aria-hidden="true" className="h-5 w-5 text-blue-600" />
        <h2 id="supplier-notifications-title" className="font-semibold text-gray-900">Notificações</h2>
      </div>
      <p className="mt-1 text-sm text-gray-500">Avisos cadastrais e documentais vinculados a este fornecedor.</p>
      {loading && <p className="mt-4 text-sm text-gray-500">Carregando notificações...</p>}
      {error && <p role="alert" className="mt-4 text-sm text-red-600">Não foi possível carregar as notificações.</p>}
      {!loading && !error && notifications.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">Nenhuma notificação para este fornecedor.</p>
      )}
      {notifications.length > 0 && (
        <ul className="mt-4 space-y-3" aria-label="Notificações do fornecedor">
          {notifications.map((notification) => {
            const payloadMessage = notification.payload?.message;
            const notificationMessage = typeof payloadMessage === 'string'
              ? payloadMessage
              : notification.type;
            return (
              <li
                key={notification.id}
                data-notification-type={notification.type}
                className={`rounded-lg border p-3 text-sm ${notification.readAt ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50'}`}
              >
                <p className="font-medium text-gray-900">{notificationMessage}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {formatNotificationDate(notification.createdAt)} · {notification.readAt ? 'Lida' : 'Não lida'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatNotificationDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return formatLocalDate(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
}

export default PortalFornecedorPage;
